import React, { useState, useEffect, useMemo } from "react";
import { useI18n, LANGUAGES } from "../i18n";
import { LanguagePicker } from "./LanguagePicker";
import { Download, Crown, Check, ChevronDown } from "lucide-react";

const QUICK = ["ta", "en", "hi", "ms", "th", "te", "ml", "kn"];
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

/* Single-screen welcome: language + 18+ consent + optional install — one tap to Home */
export default function FirstRunLanguage() {
  const { lang, changeLang } = useI18n();
  const [done, setDone] = useState(!!localStorage.getItem("rl_onboarded"));
  const [age, setAge] = useState(false);
  const [more, setMore] = useState(false);
  const [installEvt, setInstallEvt] = useState(typeof window !== "undefined" ? window.__deferredInstallPrompt : null);

  useEffect(() => {
    const h = () => setInstallEvt(window.__deferredInstallPrompt);
    window.addEventListener("pwa-installable", h);
    return () => window.removeEventListener("pwa-installable", h);
  }, []);

  const quick = useMemo(() => QUICK.map((c) => LANGUAGES.find((l) => l.code === c)).filter(Boolean), []);
  // assist-mode routes (magic link / PIN / Simple Mode): the agent already set the customer up — no reading gate, Tamil by default
  if (/^\/(pin|m\/|simple)/.test(window.location.pathname)) { if (!localStorage.getItem("rl_lang")) changeLang("ta"); return null; }
  if (done) return null;

  const finish = () => { localStorage.setItem("rl_onboarded", "1"); setDone(true); };
  const install = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    await installEvt.userChoice.catch(() => null);
    window.__deferredInstallPrompt = null;
    setInstallEvt(null);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="onboarding">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm p-5 pb-7 rl-fade-up max-h-[92vh] overflow-y-auto" data-testid="onboarding-welcome">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl rl-gold-bg flex items-center justify-center shrink-0"><Crown className="w-6 h-6" /></div>
          <div>
            <div className="font-display font-black text-lg text-slate-900 leading-tight">Welcome to ROYAL LAKSHMI</div>
            <div className="text-[11px] text-slate-500">உங்கள் மொழி · अपनी भाषा · Bahasa · ภาษา · 28 languages</div>
          </div>
        </div>

        <div data-testid="onboarding-language">
          <div className="grid grid-cols-4 gap-1.5">
            {quick.map((l) => (
              <button key={l.code} onClick={() => changeLang(l.code)} data-testid={`onboarding-lang-${l.code}`}
                className={`rounded-xl py-2 text-center border transition-colors ${lang === l.code ? "rl-gold-bg border-amber-400" : "bg-slate-50 border-slate-200 text-slate-800"}`}>
                <div className="text-lg leading-none">{l.flag}</div>
                <div className="text-[10px] font-bold truncate mt-0.5 px-1">{l.native}</div>
              </button>
            ))}
          </div>
          <button onClick={() => setMore((m) => !m)} className="mt-2 text-xs text-blue-600 font-semibold flex items-center gap-1" data-testid="onboarding-more-langs"><ChevronDown className={`w-3.5 h-3.5 transition-transform ${more ? "rotate-180" : ""}`} />{more ? "Less" : "All 28 languages"}</button>
          {more && <div className="mt-2"><LanguagePicker compact onPick={() => setMore(false)} /></div>}
        </div>

        <label className="mt-4 flex items-start gap-2.5 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 cursor-pointer" data-testid="onboarding-age-label">
          <input type="checkbox" checked={age} onChange={(e) => setAge(e.target.checked)} className="mt-0.5 w-4 h-4 accent-blue-600" data-testid="onboarding-age-check" />
          <span className="text-xs text-slate-700 leading-snug"><b>I am 18+</b> and I will play responsibly, for fun and within my limits.</span>
        </label>

        <button onClick={finish} disabled={!age} className="mt-4 w-full rl-gold-bg font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40" data-testid="onboarding-enter-btn">
          <Check className="w-4 h-4" />Enter App
        </button>

        {!isStandalone() && (
          <button onClick={installEvt ? install : undefined} className={`mt-2 w-full py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 ${installEvt ? "bg-blue-50 text-blue-700" : "text-slate-400"}`} data-testid="onboarding-install-btn">
            <Download className="w-3.5 h-3.5" />{installEvt ? "Install app on home screen (optional)" : "Tip: Install later from Me → Install App"}
          </button>
        )}
      </div>
    </div>
  );
}
